import { useCallback, useEffect, useRef, useState } from "react";
import { IoMdArrowBack } from "react-icons/io";
import { useDispatch, useSelector } from "react-redux";
import useSelectVideo from "../../hooks/useSelectVideo";
import { useNavigate, useLocation } from "react-router-dom";
import {
    emptyRecentlyDeletedPosts,
    fetchRecentlyDeletedPosts,
    restoreRecentlyDeletedPost,
    permanentlyDeleteRecentlyDeletedPost,
} from "../../redux/ApiSlice/AccountSettingSlice";
import RecentlyDeletedSkeleton from "../../components/skeleton/RecentlyDeletedSkeleton";
import { Eye, Heart, MessageCircle, Tag, RotateCcw, Trash2, Loader2 } from "lucide-react";
import { AccountProfilePosts } from "../../redux/ApiSlice/AccountProfileSlice";

const RecentlyDeleted = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const selectVideo = useSelectVideo();
    const {
        recentlyDeleted,
        recentlyDeletedLoading,
        recentlyDeletedError,
        emptyRecentlyDeletedLoading,
        recentlyDeletedMeta,

    } = useSelector((state) => state.AccountSetting);
    const ProfileDetails = useSelector((state) => state.AccountProfile);
    const {
        User_profile,
    } = ProfileDetails;

    const [modalState, setModalState] = useState({ type: null, post: null });
    const [modalLoading, setModalLoading] = useState(false);
    const [modalError, setModalError] = useState(null);
    const [isPaginationLoading, setIsPaginationLoading] = useState(false);
    const postsTabScrollRef = useRef(null);

    useEffect(() => {
        if (modalState.type) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "";
        }
        return () => {
            document.body.style.overflow = "";
        };
    }, [modalState.type]);

    useEffect(() => {
        dispatch(fetchRecentlyDeletedPosts());
    }, [dispatch]);

    const disableEmptyAction = recentlyDeletedLoading || emptyRecentlyDeletedLoading || recentlyDeleted.length === 0;

    const formatDate = (value) => {
        if (!value) {
            return "";
        }
        try {
            return new Intl.DateTimeFormat(undefined, {
                dateStyle: "medium",
                timeStyle: "short",
            }).format(new Date(value));
        } catch (error) {
            return value;
        }
    };

    const getRetentionPercent = (post) => {
        if (!post?.days_until_permanent_deletion) {
            return 0;
        }
        const percent = (post.days_until_permanent_deletion / 60) * 100;
        return Math.min(Math.max(percent, 0), 100);
    };

    const handleOpenModal = (type, post = null) => {
        setModalError(null);
        setModalState({ type, post });
    };

    const handleCloseModal = (force = false) => {
        if (modalLoading && !force) {
            return;
        }
        setModalError(null);
        setModalState({ type: null, post: null });
    };

    const handleAction = async (actionType) => {
        if (!actionType) {
            return;
        }

        setModalLoading(true);
        setModalError(null);

        try {
            if (actionType === "restore" && modalState.post?.id) {
                await dispatch(restoreRecentlyDeletedPost(modalState.post.id)).unwrap();
                await dispatch(AccountProfilePosts({ id: User_profile?.id }));
            } else if (actionType === "delete" && modalState.post?.id) {
                await dispatch(permanentlyDeleteRecentlyDeletedPost(modalState.post.id)).unwrap();
            } else if (actionType === "empty") {
                await dispatch(emptyRecentlyDeletedPosts()).unwrap();
            } else {
                throw new Error("Unsupported action.");
            }

            await dispatch(fetchRecentlyDeletedPosts());
            handleCloseModal(true);
        } catch (error) {
            const message = typeof error === "string"
                ? error
                : error?.detail || error?.message || "Something went wrong. Please try again.";
            setModalError(message);
        } finally {
            setModalLoading(false);
        }
    };

    const renderEmptyState = () => (
        <div className="rounded-3xl border border-dashed border-gray-300 dark:border-gray-800/50 bg-gradient-to-r from-gray-100 to-gray-200 dark:from-[#1E1D21] dark:to-[#1E1D21] p-8 text-center">
            <p className="text-lg font-semibold text-black dark:text-white">Nothing to restore</p>
            <p className="mt-2 text-sm text-black/70 dark:text-white">
                Delete a post to see it here. You will have 60 days to bring it back before it disappears for good.
            </p>
        </div>
    );

    const renderLoading = () => <RecentlyDeletedSkeleton />;

    const renderErrorState = () => (
        <div className="rounded-2xl border border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950/50 px-4 py-3 text-sm text-red-700 dark:text-red-200 flex flex-col gap-3">
            <span>{typeof recentlyDeletedError === "string" ? recentlyDeletedError : "Unable to load Recently Deleted. Please try again."}</span>
            <button
                onClick={() => dispatch(fetchRecentlyDeletedPosts())}
                className="self-start rounded-full bg-red-600 px-3 py-1 text-xs font-semibold text-white hover:bg-red-500"
            >
                Retry
            </button>
        </div>
    );

    const modalLabels = {
        empty: {
            title: "Empty Recently Deleted?",
            description: "Every post in Recently Deleted will be permanently deleted right away.",
            confirm: "Empty now",
            confirmClasses: "bg-red-700 hover:bg-red-600",
        },
    };

    const activeModalCopy = modalLabels[modalState.type] || null;
    const isInfoModal = modalState.type === "info";
    const isDeleteModal = modalState.type === "delete";
    const isEmptyModal = modalState.type === "empty";
    const selectedPost = modalState.post;

    const handleTileKeyDown = (event, post) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            handleOpenModal("info", post);
        }
    };

    const handleViewPost = (post) => {
        if (post?.id) {
            handleCloseModal(true);
            selectVideo(post.id, recentlyDeleted, location.pathname);
        }
    };

    const handleScroll = useCallback(() => {
        const container = postsTabScrollRef?.current;
        if (!container) {
            return;
        }

        if (isPaginationLoading || !recentlyDeletedMeta?.next) {
            return;
        }

        const hasReachedBottom =
            container.scrollTop + container.clientHeight >= container.scrollHeight - 10;

        if (hasReachedBottom) {
            setIsPaginationLoading(true);
            dispatch(
                fetchRecentlyDeletedPosts(recentlyDeletedMeta.next)
            ).finally(() => {
                setIsPaginationLoading(false);
            });
        }
    }, [dispatch, postsTabScrollRef, recentlyDeletedMeta, isPaginationLoading]);

    useEffect(() => {
        const container = postsTabScrollRef?.current;
        if (!container) return;

        container.addEventListener("scroll", handleScroll);

        return () => {
            container.removeEventListener("scroll", handleScroll);
        };
    }, [handleScroll, postsTabScrollRef]);


    return (
        <>
            <div ref={postsTabScrollRef} className="relative h-full overflow-y-auto bg-white text-black dark:bg-[#1E1D21] dark:text-white">         <div className="sticky top-0 z-50">
                <div className="relative h-14 rounded-b-2xl bg-[#BDE6FF] text-black shadow-sm">
                    <IoMdArrowBack
                        onClick={() => navigate("/settings/youractivity")}
                        className="absolute left-2 top-1/2 transition-transform -translate-y-1/2 text-[24px] text-black cursor-pointer"
                    />
                    <p className="text-center text-[16px] font-semibold leading-[56px]">Recently Deleted</p>
                </div>
            </div>

                <div className="p-3 space-y-5">
                    {!recentlyDeletedLoading && !recentlyDeletedError && recentlyDeleted.length > 0 && (
                        <div className="rounded-3xl border border-transparent bg-gradient-to-br from-gray-100 to-gray-200 dark:from-[#333333] dark:to-[#333333] p-4">
                            <p className="text-sm text-gray-700 dark:text-gray-200">
                                Posts stay here for up to 60 days after you delete them. After that, they are removed permanently.
                            </p>
                            <button
                                disabled={disableEmptyAction}
                                onClick={() => handleOpenModal("empty")}
                                className={`mt-3 text-[12px] rounded-full px-4 py-2 font-semibold transition flex items-center gap-1.5 ${disableEmptyAction
                                    ? "cursor-not-allowed bg-gray-200 text-gray-500 dark:bg-[#333333] dark:text-gray-500"
                                    : "bg-black text-white hover:bg-gray-900 dark:bg-white dark:text-black"
                                    }`}
                            >
                                {emptyRecentlyDeletedLoading ? (
                                    <>
                                        <Loader2 size={14} className="animate-spin" /> Emptying...
                                    </>
                                ) : (
                                    "Empty Recently Deleted"
                                )}
                            </button>
                        </div>
                    )}

                    {recentlyDeletedError && renderErrorState()}

                    {recentlyDeletedLoading && recentlyDeleted.length === 0 && renderLoading()}

                    {!recentlyDeletedLoading && !recentlyDeletedError && recentlyDeleted.length === 0 && renderEmptyState()}

                    {/* recently deleted posts grid */}
                    {recentlyDeleted.length > 0 && (
                        <div className="grid grid-cols-3 gap-1.5">
                            {recentlyDeleted.map((post) => (
                                <div
                                    key={post.id}
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => handleOpenModal("info", post)}
                                    onKeyDown={(event) => handleTileKeyDown(event, post)}
                                    className="group cursor-pointer rounded-lg overflow-hidden border border-gray-100 dark:border-gray-800/50 bg-gray-200 dark:bg-[#333333] text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#0EA5E9]"
                                >
                                    {/* Thumbnail */}
                                    <div className="relative aspect-[9/12] w-full overflow-hidden">
                                        {post.thumbnail_url ? (
                                            <img
                                                src={post.thumbnail_url}
                                                alt={post.post_description || "Deleted post preview"}
                                                className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                                            />
                                        ) : (
                                            <div className="flex h-full w-full items-center justify-center text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                                No preview
                                            </div>
                                        )}
                                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent opacity-60 transition group-hover:opacity-80" />
                                    </div>

                                    {/* Stats row */}
                                    <div className="flex items-center justify-between px-1.5 py-1 bg-white dark:bg-[#1E1D21]">
                                        <span className="flex items-center gap-0.5 text-[10px] text-gray-500 dark:text-gray-400">
                                            <Eye size={11} />
                                            {post.view_count ?? 0}
                                        </span>
                                        <span className="flex items-center gap-0.5 text-[10px] text-gray-500 dark:text-gray-400">
                                            {post.likes_count ?? 0}
                                            <Heart size={11} />
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Pagination loader */}
                    {isPaginationLoading && (
                        <div className="flex justify-center items-center py-8">
                            <Loader2 size={24} className="animate-spin text-gray-500 dark:text-gray-400" />
                            {/* <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">Loading more...</span> */}
                        </div>
                    )}
                </div>

            </div>
            {(isInfoModal || isDeleteModal || isEmptyModal) && (
                <div className="fixed inset-0 z-[999] flex items-center justify-center bg-black/60 px-3 py-4">
                    <div className="w-full max-w-md rounded-3xl bg-white shadow-2xl dark:bg-[#333333] max-h-[80vh] flex flex-col">
                        {isInfoModal && (
                            <>
                                <div className="flex-shrink-0 p-5 pb-3">
                                    <p className="text-xl font-semibold text-gray-900 dark:text-white">Post details</p>
                                </div>

                                <div className="flex-1 overflow-y-auto px-5">
                                    <p className="text-[14px] text-gray-900 dark:text-white break-all line-clamp-3">
                                        {selectedPost?.post_description || "Untitled post"}
                                    </p>
                                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                        Deleted {formatDate(selectedPost?.deleted_at)} • {selectedPost?.days_until_permanent_deletion || 0} days left
                                    </p>

                                    <div className="mt-4 flex flex-col gap-4 sm:flex-row sm:items-center">
                                        <div className="group relative h-[20vh] w-24 flex-shrink-0 overflow-hidden rounded-lg bg-gray-200 dark:bg-[#333333] self-center sm:self-auto cursor-pointer">
                                            {selectedPost?.thumbnail_url ? (
                                                <img
                                                    src={selectedPost.thumbnail_url}
                                                    alt={selectedPost?.post_description || "Deleted post preview"}
                                                    className="h-full w-full object-cover transition duration-300 group-hover:scale-105"
                                                />
                                            ) : (
                                                <div className="flex h-full w-full items-center justify-center text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                                    No preview
                                                </div>
                                            )}
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 transition group-hover:opacity-100" />
                                            <div className="absolute inset-0 flex items-center justify-center opacity-0 transition group-hover:opacity-100">
                                                <button
                                                    type="button"
                                                    onClick={() => handleViewPost(selectedPost)}
                                                    className="rounded-full text-[10px] bg-white/95 px-3 py-1 font-semibold text-gray-900 shadow-lg hover:bg-white transition"
                                                >
                                                    View
                                                </button>
                                            </div>
                                        </div>
                                        <div className="flex-1 text-sm break-words">
                                            <div className="flex flex-col gap-2 text-xs text-gray-500 dark:text-gray-400">
                                                <span className="flex items-center gap-1.5">
                                                    <Eye size={14} /> {selectedPost?.view_count ? `${selectedPost.view_count} views` : "No View"}
                                                </span>
                                                <span className="flex items-center gap-1.5">
                                                    <Heart size={14} /> {selectedPost?.likes_count ? `${selectedPost.likes_count} likes` : "No Like"}
                                                </span>
                                                <span className="flex items-center gap-1.5">
                                                    <MessageCircle size={14} /> {selectedPost?.comments_count ? `${selectedPost.comments_count} comments` : "No Comment"}
                                                </span>
                                                {selectedPost?.interest &&
                                                    <span className="flex items-center lowercase gap-1.5">
                                                        <Tag size={14} /> {selectedPost.interest}
                                                    </span>
                                                }
                                                <div className="flex flex-wrap items-center gap-1 mt-1">
                                                    <span
                                                        onClick={() => !modalLoading && handleAction("restore")}
                                                        className={`bg-blue-200 text-blue-900 transition duration-300 text-[10px] px-2 py-0.5 rounded-full w-fit flex items-center gap-1 ${modalLoading ? "opacity-50 cursor-not-allowed" : "hover:bg-blue-300/80 cursor-pointer"
                                                            }`}
                                                    >
                                                        {modalLoading ? (
                                                            <><Loader2 size={12} className="animate-spin" /> Recovering...</>
                                                        ) : (
                                                            <><RotateCcw size={12} /> Recover post</>
                                                        )}
                                                    </span>
                                                    <span
                                                        onClick={() => !modalLoading && setModalState({ type: "delete", post: selectedPost })}
                                                        className={`bg-red-200 text-red-900 transition duration-300 text-[10px] px-2 py-0.5 rounded-full w-fit flex items-center gap-1 ${modalLoading ? "opacity-50 cursor-not-allowed" : "hover:bg-red-300/80 cursor-pointer"
                                                            }`}
                                                    >
                                                        <Trash2 size={12} /> Delete permanently
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {modalError && (
                                        <div className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600 dark:border-red-900 dark:bg-red-900/40 dark:text-red-200">
                                            {modalError}
                                        </div>
                                    )}
                                </div>

                                <div className="p-5 pt-4 w-full">
                                    <button
                                        disabled={modalLoading}
                                        onClick={handleCloseModal}
                                        className={`rounded-full w-full px-4 py-2 text-sm font-semibold text-black dark:text-black bg-gray-200/50 dark:bg-white hover:bg-gray-200 border border-gray-300 ${modalLoading ? "opacity-70" : ""}`}
                                    >
                                        Close
                                    </button>
                                </div>
                            </>
                        )}

                        {isDeleteModal && (
                            <div className="p-5">
                                <p className="text-xl font-semibold text-gray-900 dark:text-white">Delete permanently?</p>
                                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                                    This post and its media will be erased immediately. You cannot undo this action.
                                </p>

                                {selectedPost?.post_description && (
                                    <div className="mt-4 rounded-2xl bg-gray-100 p-3 text-xs text-gray-600 dark:bg-[#333333] dark:text-gray-300 break-words">
                                        {selectedPost.post_description}
                                    </div>
                                )}

                                {modalError && (
                                    <div className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600 dark:border-red-900 dark:bg-red-900/40 dark:text-red-200">
                                        {modalError}
                                    </div>
                                )}

                                <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                                    <button
                                        disabled={modalLoading}
                                        onClick={handleCloseModal}
                                        className={`w-full rounded-full border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 dark:border-gray-800/50 dark:text-gray-200 ${modalLoading ? "opacity-70" : ""}`}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        disabled={modalLoading}
                                        onClick={() => handleAction("delete")}
                                        className={`w-full rounded-full px-4 py-2 text-sm font-semibold text-white transition bg-red-600 hover:bg-red-500 ${modalLoading ? "opacity-70" : ""}`}
                                    >
                                        {modalLoading ? "Working..." : "Delete forever"}
                                    </button>
                                </div>
                            </div>
                        )}

                        {isEmptyModal && activeModalCopy && (
                            <div className="p-5">
                                <p className="text-xl font-semibold text-gray-900 dark:text-white">{activeModalCopy.title}</p>
                                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">{activeModalCopy.description}</p>

                                {modalError && (
                                    <div className="mt-3 rounded-2xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600 dark:border-red-900 dark:bg-red-900/40 dark:text-red-200">
                                        {modalError}
                                    </div>
                                )}

                                <div className="mt-6 flex flex-col gap-3 sm:flex-row">
                                    <button
                                        disabled={modalLoading}
                                        onClick={handleCloseModal}
                                        className={`w-full rounded-full border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 transition hover:bg-gray-50 dark:border-gray-800/50 dark:text-gray-200 ${modalLoading ? "opacity-70" : ""}`}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        disabled={modalLoading}
                                        onClick={() => handleAction("empty")}
                                        className={`w-full rounded-full px-4 py-2 text-sm font-semibold text-white transition ${activeModalCopy.confirmClasses} ${modalLoading ? "opacity-70" : ""}`}
                                    >
                                        {modalLoading ? "Working..." : activeModalCopy.confirm}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    );
};

export default RecentlyDeleted;